import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import Icon from '../../components/AppIcon';
import ActiveAlertsPanel from './components/ActiveAlertsPanel';
import EscalationRulesPanel from './components/EscalationRulesPanel';
import StaffAvailabilityPanel from './components/StaffAvailabilityPanel';
import AlertStatistics from './components/AlertStatistics';
import EscalationRuleModal from './components/EscalationRuleModal';
import { twilioService } from '../../lib/twilioService';


const CriticalAlertManagementCenter = () => {
  const { userProfile } = useAuth();
  const userRole = userProfile?.role || 'manager';
  const gymId = userProfile?.gym_id;

  const [activeAlerts, setActiveAlerts] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [staffAvailability, setStaffAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [alertFilter, setAlertFilter] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');

  const loadAlertData = async () => {
    setLoading(true);
    
    // Load escalation rules
    const { data: rules } = await twilioService?.getAlertConfigurations(gymId);
    if (rules) {
      setEscalationRules(rules);
    }

    // Load active alerts (mock data with real structure)
    const mockActiveAlerts = [
      {
        id: 1,
        type: 'payment_failure',
        category: 'Financial',
        title: 'Payment Processing Failed',
        description: 'Monthly membership payment declined for Sarah Johnson',
        severity: 'critical',
        priority: 'high',
        status: 'active',
        memberName: 'Sarah Johnson',
        memberId: 'M-1234',
        amount: '$79.99',
        assignedTo: 'John Manager',
        assignedRole: 'manager',
        createdAt: new Date(Date.now() - 15 * 60000)?.toISOString(),
        escalationTimer: 45,
        escalationLevel: 1,
        maxEscalationLevel: 3,
        icon: 'CreditCard',
        location: 'Downtown Location'
      },
      {
        id: 2,
        type: 'equipment_issue',
        category: 'Safety',
        title: 'Equipment Safety Alert',
        description: 'Treadmill #5 emergency stop triggered - requires immediate inspection',
        severity: 'critical',
        priority: 'high',
        status: 'active',
        equipment: 'Treadmill #5',
        equipmentId: 'EQ-5',
        assignedTo: 'Mike Maintenance',
        assignedRole: 'staff',
        createdAt: new Date(Date.now() - 8 * 60000)?.toISOString(),
        escalationTimer: 22,
        escalationLevel: 1,
        maxEscalationLevel: 3,
        icon: 'AlertTriangle',
        location: 'Cardio Zone'
      },
      {
        id: 3,
        type: 'capacity_warning',
        category: 'Operational',
        title: 'Facility Capacity Warning',
        description: 'Current occupancy at 95% - approaching maximum capacity',
        severity: 'high',
        priority: 'medium',
        status: 'active',
        currentCapacity: 95,
        maxCapacity: 100,
        assignedTo: 'Lisa Staff',
        assignedRole: 'staff',
        createdAt: new Date(Date.now() - 5 * 60000)?.toISOString(),
        escalationTimer: 55,
        escalationLevel: 0,
        maxEscalationLevel: 2,
        icon: 'Users',
        location: 'Main Facility'
      },
      {
        id: 4,
        type: 'equipment_issue',
        category: 'Maintenance',
        title: 'Equipment Malfunction',
        description: 'Rowing machine #3 display not functioning',
        severity: 'medium',
        priority: 'medium',
        status: 'active',
        equipment: 'Rowing Machine #3',
        equipmentId: 'EQ-12',
        assignedTo: null,
        assignedRole: null,
        createdAt: new Date(Date.now() - 25 * 60000)?.toISOString(),
        escalationTimer: 35,
        escalationLevel: 0,
        maxEscalationLevel: 2,
        icon: 'Wrench',
        location: 'Cardio Zone'
      },
      {
        id: 5,
        type: 'payment_failure',
        category: 'Financial',
        title: 'Payment Retry Failed',
        description: 'Automatic payment retry failed for Michael Chen',
        severity: 'high',
        priority: 'medium',
        status: 'active',
        memberName: 'Michael Chen',
        memberId: 'M-5678',
        amount: '$99.99',
        assignedTo: 'John Manager',
        assignedRole: 'manager',
        createdAt: new Date(Date.now() - 45 * 60000)?.toISOString(),
        escalationTimer: 15,
        escalationLevel: 2,
        maxEscalationLevel: 3,
        icon: 'DollarSign',
        location: 'Downtown Location'
      }
    ];

    setActiveAlerts(mockActiveAlerts);
    setLoading(false);
  };

  const loadStaffAvailability = async () => {
    // Mock staff availability data
    const mockStaff = [
      {
        id: 1,
        name: 'John Manager',
        role: 'manager',
        status: 'available',
        currentAlerts: 2,
        avgResponseTime: '5 min',
        phone: '+1234567890',
        email: 'john@gym.com',
        lastActive: new Date(Date.now() - 2 * 60000)?.toISOString(),
        preferences: ['sms', 'email']
      },
      {
        id: 2,
        name: 'Mike Maintenance',
        role: 'staff',
        status: 'busy',
        currentAlerts: 1,
        avgResponseTime: '8 min',
        phone: '+1234567891',
        email: 'mike@gym.com',
        lastActive: new Date(Date.now() - 1 * 60000)?.toISOString(),
        preferences: ['sms']
      },
      {
        id: 3,
        name: 'Lisa Staff',
        role: 'staff',
        status: 'available',
        currentAlerts: 1,
        avgResponseTime: '6 min',
        phone: '+1234567892',
        email: 'lisa@gym.com',
        lastActive: new Date(Date.now() - 5 * 60000)?.toISOString(),
        preferences: ['email', 'sms']
      },
      {
        id: 4,
        name: 'Sarah Owner',
        role: 'owner',
        status: 'available',
        currentAlerts: 0,
        avgResponseTime: '12 min',
        phone: '+1234567893',
        email: 'sarah@gym.com',
        lastActive: new Date(Date.now() - 10 * 60000)?.toISOString(),
        preferences: ['email']
      },
      {
        id: 5,
        name: 'Tom Trainer',
        role: 'trainer',
        status: 'offline',
        currentAlerts: 0,
        avgResponseTime: '15 min',
        phone: '+1234567894',
        email: 'tom@gym.com',
        lastActive: new Date(Date.now() - 120 * 60000)?.toISOString(),
        preferences: ['sms']
      }
    ];

    setStaffAvailability(mockStaff);
  };

  // Load alert configurations and active alerts
  useEffect(() => {
    if (gymId) {
      loadAlertData();
      loadStaffAvailability();
      
      // Auto-refresh every 30 seconds
      const interval = setInterval(loadAlertData, 30000);
      return () => clearInterval(interval);
    }
  }, [gymId]);

  const handleCreateRule = () => {
    setSelectedRule(null);
    setShowRuleModal(true);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setShowRuleModal(true);
  };

  const handleSaveRule = async (ruleData) => {
    if (selectedRule) {
      // Update existing rule
      const { data, error } = await twilioService?.updateAlertConfiguration(
        selectedRule?.id,
        ruleData
      );
      
      if (!error) {
        setSuccessMessage('Escalation rule updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        loadAlertData();
      }
    } else {
      // Create new rule
      const { data, error } = await twilioService?.createAlertConfiguration(
        gymId,
        ruleData
      );
      
      if (!error) {
        setSuccessMessage('Escalation rule created successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        loadAlertData();
      }
    }
    
    setShowRuleModal(false);
  };

  const handleAssignAlert = async (alertId, staffId) => {
    // Update alert assignment
    const updatedAlerts = activeAlerts?.map(alert => {
      if (alert?.id === alertId) {
        const staff = staffAvailability?.find(s => s?.id === staffId);
        return {
          ...alert,
          assignedTo: staff?.name,
          assignedRole: staff?.role
        };
      }
      return alert;
    });
    
    setActiveAlerts(updatedAlerts);
    setSuccessMessage('Alert assigned successfully');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleResolveAlert = async (alertId) => {
    // Remove resolved alert
    setActiveAlerts(activeAlerts?.filter(alert => alert?.id !== alertId));
    setSuccessMessage('Alert resolved successfully');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleEscalateAlert = async (alertId) => {
    // Escalate alert to next level
    const updatedAlerts = activeAlerts?.map(alert => {
      if (alert?.id === alertId && alert?.escalationLevel < alert?.maxEscalationLevel) {
        return {
          ...alert,
          escalationLevel: alert?.escalationLevel + 1,
          escalationTimer: 60
        };
      }
      return alert;
    });
    
    setActiveAlerts(updatedAlerts);
    setSuccessMessage('Alert escalated to next level');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleBroadcastAlert = async (alertId) => {
    setSuccessMessage('Emergency broadcast sent to all available staff');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const filteredAlerts = activeAlerts?.filter(alert => {
    if (alertFilter === 'all') return true;
    if (alertFilter === 'critical') return alert?.severity === 'critical';
    if (alertFilter === 'high') return alert?.severity === 'high';
    if (alertFilter === 'unassigned') return !alert?.assignedTo;
    if (alertFilter === 'payment') return alert?.type === 'payment_failure';
    if (alertFilter === 'equipment') return alert?.type === 'equipment_issue';
    if (alertFilter === 'capacity') return alert?.type === 'capacity_warning';
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>
      
      <main className="p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-error/10 flex items-center justify-center">
              <Icon name="Bell" size={28} className="text-error" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Critical Alert Management Center</h1>
              <p className="text-muted-foreground">Real-time escalation workflows and intelligent staff routing</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm text-success font-medium">{successMessage}</p>
          </div>
        )}

        {/* Statistics */}
        <AlertStatistics alerts={activeAlerts} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
          {/* Active Alerts - Takes 2 columns */}
          <div className="xl:col-span-2">
            <ActiveAlertsPanel
              alerts={filteredAlerts}
              filter={alertFilter}
              onFilterChange={setAlertFilter}
              onAssign={handleAssignAlert}
              onResolve={handleResolveAlert}
              onEscalate={handleEscalateAlert}
              onBroadcast={handleBroadcastAlert}
              staffAvailability={staffAvailability}
              loading={loading}
            />
          </div>

          {/* Staff Availability */}
          <div>
            <StaffAvailabilityPanel staff={staffAvailability} />
          </div>
        </div>

        {/* Escalation Rules */}
        <div className="mt-6">
          <EscalationRulesPanel
            rules={escalationRules}
            onCreateRule={handleCreateRule}
            onEditRule={handleEditRule}
          />
        </div>
      </main>

      {/* Escalation Rule Modal */}
      {showRuleModal && (
        <EscalationRuleModal
          rule={selectedRule}
          onClose={() => setShowRuleModal(false)}
          onSave={handleSaveRule}
        />
      )}
    </div>
  );
};

export default CriticalAlertManagementCenter;